<script setup lang="ts">
import { computed } from 'vue';
import { store } from '@/app/store';
import { useSessionPassFonts } from '@/features/session-pass/useSessionPassFonts';
import { resolveDownloadTheme } from './download-theme';
import type { CustomerSessionPassData } from './useCustomerDownloadCodePage';

const props = defineProps<{
  sessionPass: CustomerSessionPassData | null;
  hasExtras?: boolean;
}>();
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const locale = computed(() => store.currentLocale);
const theme = computed(() =>
  props.sessionPass ? resolveDownloadTheme(props.sessionPass) : null
);
useSessionPassFonts(theme);
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const style = computed(() => {
  if (!theme.value) {
    return {};
  }
  const t = theme.value;
  return {
    '--Download-background': t.background,
    '--Download-wallpaper': t.wallpaperUrl
      ? `url("${t.wallpaperUrl}")`
      : 'none',
    '--Download-display-font': t.displayFont,
    '--Download-body-font': t.bodyFont,
    '--Extras-surface': t.surface,
    '--Extras-surface-strong': t.surfaceStrong,
    '--Extras-text': t.text,
    '--Extras-muted': t.textMuted,
    '--Extras-accent': t.accent,
    '--Extras-secondary': t.accentSecondary,
    '--Extras-on-accent': t.onAccent,
  };
});
</script>

<template>
  <div v-if="sessionPass && theme" class="DownloadLayout" :data-theme="theme.id" :style="style">
    <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <div class="DownloadLayout__inner">
      <header class="DownloadLayout__header">
        <img class="DownloadLayout__logo" :src="theme.logoUrl" :alt="sessionPass.kioskName || 'TheArtisan'">
        <img v-if="locale?.icon" class="DownloadLayout__language" :src="locale.icon" :alt="locale.title">
      </header>
      <section class="DownloadLayout__visit">
        <h1>{{ $t(hasExtras ? 'CustomerDigitalExtras.downloadTitle' : 'CustomerDigitalExtras.photosTitle') }}</h1>
        <p class="DownloadLayout__intro">{{ $t(hasExtras ? 'CustomerDigitalExtras.downloadIntro' : 'CustomerDigitalExtras.photosIntro') }}</p>
      </section>
      <slot />
    </div>
  </div>
  <slot v-else />
</template>

<style scoped>
.DownloadLayout {
  --Extras-panel: color-mix(in srgb, var(--Extras-surface) 82%, transparent);
  --Extras-line: color-mix(in srgb, var(--Extras-surface) 75%, transparent);
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  color: var(--Extras-text);
  background: var(--Download-wallpaper) center top / cover no-repeat, var(--Download-background);
  font: 400 14px / 1.5 var(--Download-body-font);
  text-shadow: none;
  -webkit-font-smoothing: antialiased;
}
.DownloadLayout__inner { width: min(100%, 980px); margin-inline: auto; padding: 0 18px 36px; }
.DownloadLayout__header { height: 88px; display: flex; align-items: center; justify-content: space-between; padding-inline: 2px; gap: 24px; }
.DownloadLayout__logo { width: 150px; height: 66px; object-fit: contain; object-position: left center; }
.DownloadLayout__language { width: 35px; height: 24px; object-fit: cover; }
.DownloadLayout__visit { padding: 10px 3px 24px; }
.DownloadLayout p { margin: 0; }
.DownloadLayout h1 { margin: 0; font: 800 30px / 1.12 var(--Download-display-font); font-variation-settings: 'wght' 800; letter-spacing: -1px; }
.DownloadLayout .DownloadLayout__intro { margin-top: 12px; color: var(--Extras-muted); font-size: 13px; line-height: 1.6; }
.DownloadLayout :deep(.ImageGallery) { grid-template-rows: max-content; }
.DownloadLayout :deep(.ImageGallery__item-wrapper) { display: flex; flex-direction: column; height: auto; aspect-ratio: auto; border-radius: 16px; background: var(--Extras-panel); }
.DownloadLayout :deep(.ImageGallery__item-image) { position: static; order: 0; height: auto; aspect-ratio: var(--CustomerPortraitDownload--item-ratio); }
.DownloadLayout :deep(.ImageGallery__item-overlay) { position: relative; order: 1; inset: auto; }
.DownloadLayout :deep(.ImageGallery__item[data-loading='true'] .ImageGallery__item-overlay),
.DownloadLayout :deep(.ImageGallery__item[data-error='true'] .ImageGallery__item-overlay) { position: absolute; inset: 0; }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__buttons) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; padding: 12px; }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__buttons::before) { display: none; }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__buttons-button) { min-height: 44px; height: auto; padding: 10px 8px; border-radius: 99px; font: 600 11px / 1.3 var(--Download-body-font); text-transform: none; white-space: normal; box-shadow: none; color: var(--Extras-on-accent); background: var(--Extras-accent); }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__buttons .CustomerPortraitsDownloadCode__buttons-button) { width: 100%; }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__action-status) { grid-column: 1 / -1; color: var(--Extras-text); font: 400 12px / 1.4 var(--Download-body-font); text-shadow: none; }
.DownloadLayout :deep(.CustomerPortraitsDownloadCode__buttons-button--secondary) { color: var(--Extras-text); background: var(--Extras-surface); }
.DownloadLayout :deep(.CustomerSessionPass) { width: min(520px, 100%); margin: 0 auto; border: 1px solid var(--Extras-line); border-radius: 20px; background: var(--Extras-panel); }
.DownloadLayout :deep(.CustomerSessionPass__content) { padding: 18px; background: transparent; }
.DownloadLayout :deep(.CustomerSessionPass__header),
.DownloadLayout :deep(.CustomerSessionPass__intro),
.DownloadLayout :deep(.CustomerSessionPass__footer) { display: none; }
.DownloadLayout :deep(.CustomerSessionPass__ticket) { padding: 0; }
.DownloadLayout :deep(.CustomerSessionPass__scan-label) { margin-bottom: 12px; color: var(--Extras-muted); font-size: 11px; }
.DownloadLayout :deep(.CustomerSessionPass__qr-frame) { width: min(224px, 100%); }
.DownloadLayout :deep(.CustomerSessionPass__code-block) { width: 100%; margin-top: 12px; padding: 12px 0 0; gap: 12px; border-top: 1px solid var(--Extras-line); border-bottom: 0; }
.DownloadLayout :deep(.CustomerSessionPass__code-block span) { color: var(--Extras-muted); font-size: 11px; }
.DownloadLayout :deep(.CustomerSessionPass__code-block strong) { color: var(--Extras-accent); font-size: 15px; }
@media (min-width: 720px) {
  .DownloadLayout__inner { padding: 12px 32px 48px; }
  .DownloadLayout__visit { padding: 16px 0 28px; }
  .DownloadLayout h1 { font-size: 38px; }
}
</style>
